'use client'

import Image from 'next/image'
import Link from 'next/link'
import type { Project } from '@/lib/projects'

/**
 * Portfolio card used by the home "Selected work" grid and the
 * dedicated /work page. Pure presentational — relies entirely on the
 * Project record's fields.
 *
 * Link priority:
 *   1. `caseStudy` slug → opens the internal case study page via next/link.
 *   2. `url` → opens the external live site in a new tab.
 *   3. neither → renders a non-interactive <div>.
 */
export default function ProjectCard({
  project,
  dark = false,
}: {
  project: Project
  dark?: boolean
}) {
  const imageWrapClass = dark ? 'bg-charcoal-900' : 'bg-charcoal-100'
  const tagClass = dark
    ? 'text-white/70 border-white/15'
    : 'text-charcoal-600 border-charcoal-200'
  const titleClass = dark ? 'text-white' : 'text-charcoal-800'
  const descClass = dark ? 'text-white/60' : 'text-charcoal-400'

  // Card body is identical for every link variant — extracted to keep the
  // wrapper-branching below readable.
  const isInteractive = Boolean(project.caseStudy || project.url)
  const wrapperClass = `block after:hidden ${isInteractive ? '' : 'cursor-default'}`
  const body = (
    <>
      <div className={`project-image-wrap relative overflow-hidden rounded-2xl aspect-[4/3] mb-5 ${imageWrapClass}`}>
        <Image
          src={project.image}
          alt={`${project.title} project by Soleno studio`}
          fill
          sizes="(max-width: 768px) 100vw, 50vw"
          className="object-cover"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {project.tags.map((tag) => (
          <span
            key={tag}
            className={`px-3 py-1 text-xs tracking-wide border rounded-full ${tagClass}`}
          >
            {tag}
          </span>
        ))}
      </div>

      <h3 className={`text-xl font-semibold mb-2 ${titleClass}`}>
        {project.title}
      </h3>
      <p className={`text-sm leading-relaxed line-clamp-2 ${descClass}`}>
        {project.description}
      </p>
    </>
  )

  return (
    <article className="group">
      {project.caseStudy ? (
        <Link href={project.caseStudy} className={wrapperClass}>
          {body}
        </Link>
      ) : project.url ? (
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className={wrapperClass}
        >
          {body}
        </a>
      ) : (
        <div className={wrapperClass}>{body}</div>
      )}
    </article>
  )
}
